import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, GraduationCap, Search, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { getInitials } from "@/lib/image";
import AdminNav from "@/components/AdminNav";
import FacultyEditDialog from "@/components/FacultyEditDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFaculty() {
  const { user, authHeader } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/faculty`);
      setList(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load faculty");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((f) => !q || f.name.toLowerCase().includes(q) || (f.role || "").toLowerCase().includes(q));
  }, [list, query]);

  const onCreate = () => { setEditing({}); setOpen(true); };
  const onEdit = (m) => { setEditing(m); setOpen(true); };
  const onDelete = async (m) => {
    if (!confirm(`Delete "${m.name}" permanently?`)) return;
    setDeletingId(m.id);
    try {
      await axios.delete(`${API}/faculty/${m.id}`, { headers: authHeader });
      setList((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Faculty member removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]"><Loader2 className="w-8 h-8 animate-spin text-[#002570]" /></div>;
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <AdminNav />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Admin Dashboard</p>
            <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Faculty</h1>
            <p className="mt-2 font-inter text-sm text-slate-600">Add, edit and reorder the mentors that appear on your public Faculty page.</p>
          </div>
          <button onClick={onCreate} data-testid="faculty-add-btn" className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-5 py-3 text-sm font-inter font-medium transition-colors">
            <Plus className="w-4 h-4" /> Add Faculty
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-faculty-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or role…"
              className="w-full bg-[#f8fafc] rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm text-center py-20 px-6">
            <GraduationCap className="w-10 h-10 mx-auto text-[#73c3ff]" />
            <p className="mt-4 font-rubik text-lg font-semibold text-[#0f172a]">No faculty yet</p>
            <p className="mt-1 font-inter text-sm text-slate-500">Click "Add Faculty" to add your first mentor.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-faculty-grid">
            {filtered.map((f) => (
              <div key={f.id} data-testid={`admin-faculty-card-${f.id}`} className={`bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-lg transition-all ${!f.featured ? "opacity-70" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-[#002570] to-[#001a52] text-white flex items-center justify-center font-rubik text-lg font-bold shrink-0">
                    {f.photo ? <img src={f.photo} alt={f.name} className="w-full h-full object-cover" /> : <span>{getInitials(f.name)}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-rubik text-base font-bold text-[#0f172a] truncate">{f.name}</p>
                    {f.role && <p className="font-inter text-xs text-[#002570] mt-0.5 truncate">{f.role}</p>}
                    {f.qualifications && <p className="font-inter text-xs text-slate-500 mt-1 truncate">{f.qualifications}</p>}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    {f.featured ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {f.featured ? "Public" : "Hidden"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(f)} data-testid={`admin-faculty-edit-${f.id}`} className="w-9 h-9 rounded-full bg-[#002570]/10 text-[#002570] hover:bg-[#002570] hover:text-white flex items-center justify-center transition-colors" aria-label="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(f)} disabled={deletingId === f.id} data-testid={`admin-faculty-delete-${f.id}`} className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center transition-colors disabled:opacity-60" aria-label="Delete">
                      {deletingId === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 font-inter text-xs text-slate-400 text-center">Showing {filtered.length} of {list.length} faculty members</p>
      </main>

      <FacultyEditDialog open={open} onOpenChange={setOpen} member={editing && editing.id ? editing : null} authHeader={authHeader} onSaved={fetchAll} />
    </div>
  );
}
